import { useEffect, useRef, useCallback } from 'react'
import { fetchDesignRun } from '../api/designApi'

const POLL_INTERVAL_MS = 10000
const TERMINAL_STATUSES = new Set(['completed', 'failed'])
const MAX_NETWORK_FAILURES = 5  // stop after 5 consecutive network errors (~50s)

/**
 * Polls a design run every 3s until it reaches a terminal status.
 * Calls onUpdate(run) on each poll, onDone(run) when terminal.
 */
export function useDesignPolling({ runId, enabled = true, onUpdate, onDone }) {
  const timerRef   = useRef(null)
  const stoppedRef = useRef(false)

  // Store callbacks in refs so they're always current but never cause the
  // effect to restart (inline arrow functions change reference every render).
  const onUpdateRef = useRef(onUpdate)
  const onDoneRef   = useRef(onDone)
  onUpdateRef.current = onUpdate
  onDoneRef.current   = onDone

  const stop = useCallback(() => {
    stoppedRef.current = true
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!runId || !enabled) return

    stoppedRef.current = false
    let networkFailures = 0

    const poll = async () => {
      if (stoppedRef.current) return
      try {
        const run = await fetchDesignRun(runId)
        networkFailures = 0
        onUpdateRef.current?.(run)
        if (TERMINAL_STATUSES.has(run.status)) {
          onDoneRef.current?.(run)
          return
        }
      } catch (err) {
        // 4xx means the resource is gone — stop polling.
        if (err.status >= 400 && err.status < 500) {
          stop()
          return
        }
        // Network failure — stop after too many consecutive failures.
        networkFailures++
        if (networkFailures >= MAX_NETWORK_FAILURES) {
          stop()
          return
        }
      }
      if (!stoppedRef.current) {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS)
      }
    }

    poll()
    return stop
  }, [runId, enabled, stop]) // onUpdate/onDone intentionally omitted — held in refs

  return { stop }
}
